
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ComponentPublicInstance } from 'vue';
import { lsTest } from '../../helpers/localstorage';
import TransitionExpand from '../../components/TransitionExpand.vue';
import Tab from '../../components/Tab.vue';
import HokSwitch from '../../components/HokSwitch.vue';
import Tabs from '../../components/Tabs.vue';

const CookieBannerInformation = () => import('./CookieBannerInformation.vue');

function hasSelectTab(tab: any): tab is { selectTab(tab: string): void } {
	return tab && typeof tab.selectTab === 'function';
}

function hasToggleItem(obj: any): obj is { toggleItem(id: string): void } {
	return obj && typeof obj.toggleItem === 'function';
}

function isHTMLElement(element: any): element is HTMLElement {
	return element && element instanceof HTMLElement;
}

function hasVueElement(vue: any): vue is ComponentPublicInstance {
	return vue && !!vue.$el;
}

@Component({
	name: 'CookieBanner',
	components: {
		CookieBannerInformation,
		Tab,
		Tabs,
		HokSwitch,
		TransitionExpand
	}
})
export default class CookieBanner extends Vue {
	@Prop({
		type: String,
		default: 'overview',
		validator: (value: string) => ['overview', 'config'].includes(value)
	})
	readonly stage!: String;

	@Prop({
		type: String,
		required: false,
		validator: (value: string) => ['user', 'company'].includes(value)
	})
	readonly mode?: String;

	@Watch('internalStage')
	onInternalStageChange() {
		this.$nextTick(() => {
			const bannerWrapper = this.$refs.bannerWrapper as HTMLElement | null;
			bannerWrapper?.focus();
		});
	}

	currentTabId: 'category' | 'information' = 'category';

	internalStage = this.stage;

	trackerCategories: {
		essential: boolean;
		externalYoutube: boolean;
		ads: boolean;
		stats: boolean;
	} = {
		essential: true,
		externalYoutube: false,
		ads: false,
		stats: false
	};

	accordionCategories: {
		essential: boolean;
		externalYoutube: boolean;
		ads: boolean;
		stats: boolean;
	} = {
		essential: false,
		externalYoutube: false,
		ads: false,
		stats: false
	};

	categories = [
		{
			id: 'essential',
			title: 'Essenziell',
			text: 'Diese Technologien sind unbedingt notwendig um die angeforderten Services auf unserer Website zu erbringen.',
			trackers: [
				{ name: 'Google Maps', id: 'gm' },
				{ name: 'Google Tag Manager', id: 'gtm' },
				{ name: 'Hubspot', id: 'hubspot' },
				{ name: 'Google reCaptcha', id: 'recaptcha' }
			]
		},
		{
			id: 'externalYoutube',
			title: 'Anzeige externer Inhalte',
			text: 'Diese Technologien sind notwendig um gewisse externe Inhalte auf unserer Plattform anzuzeigen (z.B.: YouTube Videos)',
			trackers: [{ name: 'YouTube', id: 'youtube' }]
		},
		{
			id: 'ads',
			title: 'Personalisierte Anzeigen und Marketing',
			text: 'Diese Technologien werden verwendet um relevantere Anzeigen zu schalten, die Performance der Anzeigen zu messen und unsere Drittpartner können damit die Werbung für dich personalisieren.',
			trackers: [
				{ name: 'Facebook Pixel', id: 'facebook' },
				{ name: 'Google Advertising', id: 'gadv' },
				{ name: 'TikTok Advertising', id: 'tiktok' },
				{ name: 'Snap Pixel', id: 'snap' },
				{ name: 'Linkedin Tag', id: 'linkedin' },
				{ name: 'Bing Ads UET Tag', id: 'bing' },
				{ name: 'Reddit Ads Conversion Tracking', id: 'reddit' },
				{ name: 'Criteo Tag', id: 'criteo' },
				{ name: 'RTB House', id: 'rtbhouse' }
			]
		},
		{
			id: 'stats',
			title: 'Statistik',
			text: 'Diese Technologien werden verwendet um Informationen über die Nutzung unserer Plattform zu erhalten um das Nutzererlebnis zu verbessern.',
			trackers: [
				{ name: 'Google Analytics', id: 'ga' },
				{ name: 'Hotjar', id: 'hotj' },
				{ name: 'Adjust', id: 'adjust' }
			]
		}
	];

	data() {
		let externalYoutube = false;
		let ads = false;
		let stats = false;

		const acceptedCookie = lsTest() && localStorage.getItem('accept-cookies');
		if (acceptedCookie && acceptedCookie.startsWith('[')) {
			const parsedCookie = JSON.parse(acceptedCookie);
			if (parsedCookie.includes('externalYoutube')) {
				externalYoutube = true;
			}
			if (parsedCookie.includes('ads')) {
				ads = true;
			}
			if (parsedCookie.includes('stats')) {
				stats = true;
			}
		}
		return {
			trackerCategories: {
				essential: true,
				externalYoutube,
				ads,
				stats
			}
		};
	}

	mounted() {
		this.$trackGenericEvent('cookie_banner_viewed', {});

		if (this.stage === 'config') {
			this.$nextTick(() => {
				const element = this.$refs.externalYoutube;
				if (element && hasVueElement(element?.[0])) {
					element[0].$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			});
			setTimeout(() => {
				this.trackerCategories.externalYoutube = true;
			}, 500); // consider this as an animation
		}
	}

	acceptAll() {
		if (lsTest()) {
			localStorage.setItem(
				'accept-cookies',
				JSON.stringify(['essential', 'externalYoutube', 'ads', 'stats'])
			);
		}
		this.$trackGenericEvent('cookie_banner_accepted', {});
		this.$emit('close');
	}

	acceptEssential() {
		if (lsTest()) {
			localStorage.setItem('accept-cookies', JSON.stringify(['essential']));
		}
		this.$emit('close');
	}

	saveConfig() {
		if (lsTest()) {
			const allowed: string[] = [];

			Object.entries(this.trackerCategories).forEach(([key, value]) => {
				if (value) {
					allowed.push(key);
				}
			});

			localStorage.setItem('accept-cookies', JSON.stringify(allowed));
		}
		this.$emit('close');
	}

	changeTabId(newTab) {
		this.currentTabId = newTab?.tab?.id || newTab;
	}

	toggleItem(id: string) {
		this.categories.some(category => {
			if (category.id === id) {
				this.accordionCategories[id] = !this.accordionCategories[id];
				return true;
			}
			return false;
		});
	}

	goTo(trackerId) {
		this.currentTabId = 'information';
		this.selectTab('information');

		// wait for CookieBannerInformation & thirdParty.json to be loaded
		// TODO: wait for an emit from the mounted lifecycle inside the tab
		setTimeout(() => {
			// wait for tab to be changed
			this.$nextTick(() => {
				const { cookieBannerInformation } = this.$refs;
				if (hasVueElement(cookieBannerInformation)) {
					const element = cookieBannerInformation.$refs[trackerId];
					if (element && isHTMLElement(element?.[0])) {
						element[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				}
			});
			if (hasToggleItem(this.$refs.cookieBannerInformation)) {
				this.$refs.cookieBannerInformation.toggleItem(trackerId);
			}
		}, 500);
	}

	selectTab(tab) {
		if (hasSelectTab(this.$refs.cookieTabs)) {
			this.$refs.cookieTabs.selectTab(tab);
			this.changeTabId(tab);
		}
	}
}
